// Entry point — published as UMD `widget.js`.
// Binds the public API to `window.BAAssistant`. Idempotent: a second
// `<script>` include reuses the same lifecycle instance.

import { createLifecycle, type Lifecycle } from './application/lifecycle';
import { readScriptConfig } from './infrastructure/scriptConfig';
import type { PublicApi } from './domain';

declare global {
  interface Window {
    BAAssistant?: PublicApi & { __lifecycle?: Lifecycle };
  }
}

function bind(): PublicApi {
  const existing = window.BAAssistant;
  if (existing !== undefined && existing.__lifecycle !== undefined) {
    return existing;
  }
  const lifecycle = createLifecycle();
  const api: PublicApi & { __lifecycle: Lifecycle } = {
    init: (opts) => lifecycle.init(opts),
    open: (mode) => lifecycle.open(mode),
    close: () => lifecycle.close(),
    destroy: () => lifecycle.destroy(),
    updateUser: (user) => lifecycle.updateUser(user),
    updateContext: (ctx) => lifecycle.updateContext(ctx),
    updateTheme: (theme) => lifecycle.updateTheme(theme),
    themes: () => lifecycle.themes(),
    __lifecycle: lifecycle,
  };
  window.BAAssistant = api;
  return api;
}

/**
 * FEATURE-86 — zero-JS auto-init. If the launcher `<script>` tag carries
 * `data-ba-*` attributes, initialize immediately so a low-tech host writes no
 * JavaScript. Fires at most once: skipped if the host already called `init()`
 * (or a duplicate script include already auto-inited). An explicit
 * `BAAssistant.init(...)` after this still applies — lifecycle.init replaces
 * state and re-flushes.
 */
function maybeAutoInit(): void {
  try {
    const lifecycle = window.BAAssistant?.__lifecycle;
    if (lifecycle !== undefined && lifecycle.__debug().init !== null) return;
    const auto = readScriptConfig();
    if (auto !== null) window.BAAssistant?.init(auto.options);
  } catch (err) {
    // Auto-init must never break the host page; manual init() stays available.
    // eslint-disable-next-line no-console
    console.error('[BAAssistant] auto-init failed:', err);
  }
}

const api = bind();
maybeAutoInit();
export default api;
export { createLifecycle };
export type { PublicApi, InitOptions, WidgetMode } from './domain';
